import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { IFederalState } from '../../Interfaces/IAemter';
import {
  federalStateLoginSchema,
  IFederalStateLogin,
} from '../../Interfaces/IFederalStateLogin';
import { IUser } from '../../Interfaces/IUser';
import { CustomeTextField } from '../../generic/CustomeTextField';
import {
  useCheckFederalStateLogin,
  useCreateFedearalStateLogin,
  useUpdateFedearalStateLogin,
} from '../../hooks/reactQuerys/FederalStateLoginQueries';
import { useAlert } from '../../context/AlertContext';

interface IProps {
  open: boolean;
  onClose: () => void;
  federalState: IFederalState;
  users: IUser[];
  /** Vorhandene Erstanmeldung zum Bearbeiten; undefined = neu anlegen. */
  existing?: IFederalStateLogin;
  refetchFederalStateLogins: Function;
}

/**
 * Anlegen und Bearbeiten einer Erstanmeldung (erste Login-Stufe, isFirstStage).
 * Bewusst getrennt vom Zugang-Dialog. Beim Bearbeiten bleibt das Passwort leer
 * und wird nur überschrieben, wenn ein neues eingegeben wird.
 */
export const ErstanmeldungDialog: React.FC<IProps> = ({
  open,
  onClose,
  federalState,
  users,
  existing,
  refetchFederalStateLogins,
}) => {
  const { showAlert } = useAlert();
  const isEdit = !!existing;

  const [erstLogin, setErstLogin] = useState<IFederalStateLogin>(
    existing
      ? federalStateLoginSchema.parse({ ...existing, Password: '' })
      : federalStateLoginSchema.parse({
          isFirstStage: true,
          idFederalState: federalState.idFederalState,
        })
  );

  const { createFederalStateLogin, isPendingCreateFederalStateLogin } =
    useCreateFedearalStateLogin();
  const { updateFederalStateLogin, isPendingUpdateFederalStateLogin } =
    useUpdateFedearalStateLogin();
  const { checkFederalStateLogin, isPendingCheckFederalStateLogin } =
    useCheckFederalStateLogin();
  const isPending =
    isPendingCreateFederalStateLogin || isPendingUpdateFederalStateLogin;

  const [isTesting, setIsTesting] = useState(false);
  const [wasSuccessfully, setWasSuccessfully] = useState(false);

  const handleTest = async () => {
    setIsTesting(true);
    try {
      const returnObject = await checkFederalStateLogin([
        federalStateLoginSchema.parse({ ...erstLogin, isFirstStage: true }),
      ]);
      setWasSuccessfully(returnObject['Successfully']);
    } catch (error) {
      console.error(error);
      setWasSuccessfully(false);
    }
  };

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const uploadObject = federalStateLoginSchema.parse({
        ...erstLogin,
        isFirstStage: true,
        idFederalState: federalState.idFederalState,
      });
      if (isEdit) {
        await updateFederalStateLogin(uploadObject);
      } else {
        await createFederalStateLogin(uploadObject);
      }
      showAlert({ text: 'Erfolgreich gespeichert!', severity: 'success' });
      refetchFederalStateLogins();
      onClose();
    } catch (error) {
      console.error(error);
      showAlert({ text: 'Es ist ein Fehler aufgetreten!', severity: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='xs' fullWidth>
      <DialogTitle>
        {isEdit ? 'Erstanmeldung bearbeiten' : 'Erstanmeldung anlegen'}
      </DialogTitle>
      <form onSubmit={handleSave}>
        <DialogContent>
          <Collapse in={isTesting}>
            {isPendingCheckFederalStateLogin ? (
              <CircularProgress />
            ) : wasSuccessfully ? (
              <Alert severity='success'>Die Anmeldung war erfolgreich!</Alert>
            ) : (
              <Alert severity='warning'>
                Eine Anmeldung war nicht möglich! Bitte überprüfen Sie Ihre
                Anmeldeinformationen.
              </Alert>
            )}
            <Box sx={{ mb: 2 }} />
          </Collapse>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant='body2' color='text.secondary'>
                Die Erstanmeldung ist die erste Login-Stufe (z. B. Brandenburg)
                und wird automatisch ausgeführt. Sie wird einem Notar
                zugeordnet.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                variant='outlined'
                label='Notar'
                fullWidth
                value={
                  erstLogin.Notary_idUser < 0 ? '' : erstLogin.Notary_idUser
                }
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setErstLogin({
                    ...erstLogin,
                    Notary_idUser: Number(event.target.value),
                  })
                }
                required
                select
                size='small'
              >
                {users
                  .filter(user => user.isNotary == true)
                  .map(user => (
                    <MenuItem
                      key={`erst-notar-${user.idUser}`}
                      value={user.idUser}
                    >
                      {`${user.FirstName} ${user.LastName}`}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <CustomeTextField
                attr='Username'
                label='Anmeldename'
                object={erstLogin}
                setObject={setErstLogin}
                type='string'
                required
              />
            </Grid>
            <Grid item xs={12}>
              <CustomeTextField
                attr='Password'
                label={isEdit ? 'Passwort (leer = unverändert)' : 'Passwort'}
                object={erstLogin}
                setObject={setErstLogin}
                type='password'
                required={!isEdit}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant='outlined'
            disabled={isPendingCheckFederalStateLogin}
            onClick={handleTest}
          >
            Testen
          </Button>
          <Button variant='outlined' onClick={onClose}>
            Abbruch
          </Button>
          <Button variant='contained' type='submit' disabled={isPending}>
            {isPending ? (
              <Box display='flex' alignItems='center' gap={1}>
                <span>Speichern</span>
                <CircularProgress size={20} />
              </Box>
            ) : (
              'Speichern'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
